import React, { useState, useEffect } from 'react';
import { clientsApi, Client } from '../lib/api';
import { toast } from 'react-hot-toast';

interface ClientFormData {
  name?: string;
  brand?: string;
  metaAccountId?: string;
  pageId?: string;
  pageName?: string;
  pixelId?: string;
  instagramAccountId?: string;
  defaultBudget?: number;
  defaultDuration?: number;
  defaultCountries?: string[];
  defaultAgeMin?: number;
  defaultAgeMax?: number;
  defaultGenders?: number[];
  primaryColor?: string;
  secondaryColor?: string;
  website?: string;
  industry?: string;
  utmTemplate?: string;
}

const ClientPresetsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [dbClients, setDbClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    brand: '',
    metaAccountId: '',
    pageId: '',
    pageName: '',
    pixelId: '',
    instagramAccountId: '',
    defaultBudget: 100,
    defaultDuration: 30,
    defaultCountries: ['US'],
    defaultAgeMin: 18,
    defaultAgeMax: 65,
    defaultGenders: [1, 2],
    primaryColor: 'var(--color-systemBlue)',
    secondaryColor: 'var(--color-systemIndigo)',
    website: '',
    industry: '',
    utmTemplate: 'utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}'
  });

  // Fetch database clients on component mount
  useEffect(() => {
    fetchDbClients();
  }, []);

  const fetchDbClients = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getClients();
      setDbClients(response.data.clients);
    } catch (error) {
      console.error('Error fetching database clients:', error);
      toast.error('Failed to fetch clients from database');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      // Convert database client to form format
      setFormData({
        name: client.name,
        brand: client.brand,
        metaAccountId: client.meta_account_id,
        pageId: client.page_id,
        pageName: client.page_name,
        pixelId: client.pixel_id,
        instagramAccountId: client.instagram_account_id || '',
        defaultBudget: client.default_budget / 100, // Convert from cents to dollars
        defaultDuration: client.default_duration,
        defaultCountries: client.default_countries,
        defaultAgeMin: client.default_age_min,
        defaultAgeMax: client.default_age_max,
        defaultGenders: client.default_genders,
        primaryColor: client.primary_color,
        secondaryColor: client.secondary_color,
        website: client.website || '',
        industry: client.industry || '',
        utmTemplate: client.utm_template || 'utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}'
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        brand: '',
        metaAccountId: '',
        pageId: '',
        pageName: '',
        pixelId: '',
        instagramAccountId: '',
        defaultBudget: 100,
        defaultDuration: 30,
        defaultCountries: ['US'],
        defaultAgeMin: 18,
        defaultAgeMax: 65,
        defaultGenders: [1, 2],
              primaryColor: 'var(--color-systemBlue)',
      secondaryColor: 'var(--color-systemIndigo)',
        website: '',
        industry: '',
        utmTemplate: 'utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}'
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (editingClient) {
        // Update database client
        const clientData = {
          name: formData.name!,
          brand: formData.brand!,
          logo_url: '',
          meta_account_id: formData.metaAccountId!,
          page_id: formData.pageId!,
          page_name: formData.pageName!,
          pixel_id: formData.pixelId!,
          instagram_account_id: formData.instagramAccountId || '',
          default_budget: (formData.defaultBudget || 100) * 100, // Convert to cents
          default_duration: formData.defaultDuration || 30,
          default_countries: formData.defaultCountries || ['US'],
          default_age_min: formData.defaultAgeMin || 18,
          default_age_max: formData.defaultAgeMax || 65,
          default_genders: formData.defaultGenders || [1, 2],
          primary_color: formData.primaryColor || 'var(--color-systemBlue)',
          secondary_color: formData.secondaryColor || 'var(--color-systemIndigo)',
          website: formData.website || '',
          industry: formData.industry || '',
          utm_template: formData.utmTemplate || ''
        };
        
        await clientsApi.updateClient(editingClient.id, clientData);
        toast.success('Client updated successfully!');
      } else {
        // Create new database client
        const clientData = {
          name: formData.name!,
          brand: formData.brand!,
          logo_url: '',
          meta_account_id: formData.metaAccountId!,
          page_id: formData.pageId!,
          page_name: formData.pageName!,
          pixel_id: formData.pixelId!,
          instagram_account_id: formData.instagramAccountId || '',
          default_budget: (formData.defaultBudget || 100) * 100, // Convert to cents
          default_duration: formData.defaultDuration || 30,
          default_countries: formData.defaultCountries || ['US'],
          default_age_min: formData.defaultAgeMin || 18,
          default_age_max: formData.defaultAgeMax || 65,
          default_genders: formData.defaultGenders || [1, 2],
          primary_color: formData.primaryColor || 'var(--color-systemBlue)',
          secondary_color: formData.secondaryColor || 'var(--color-systemIndigo)',
          website: formData.website || '',
          industry: formData.industry || '',
          utm_template: formData.utmTemplate || ''
        };
        
        await clientsApi.createClient(clientData);
        toast.success('Client created successfully!');
      }
      
      // Refresh database clients
      await fetchDbClients();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving client:', error);
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (client: Client) => {
    if (!confirm(`Are you sure you want to delete client "${client.brand}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      await clientsApi.deleteClient(client.id);
      toast.success('Client deleted successfully!');
      await fetchDbClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ClientFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (country: string, checked: boolean) => {
    const currentCountries = formData.defaultCountries || [];
    if (checked) {
      handleInputChange('defaultCountries', [...currentCountries, country]);
    } else {
      handleInputChange('defaultCountries', currentCountries.filter(c => c !== country));
    }
  };

  const handleGenderChange = (gender: number, checked: boolean) => {
    const currentGenders = formData.defaultGenders || [];
    if (checked) {
      handleInputChange('defaultGenders', [...currentGenders, gender]);
    } else {
      handleInputChange('defaultGenders', currentGenders.filter(g => g !== gender));
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-systemGroupedBackground)', padding: 'var(--spacing-8) 0' }}>
      <div style={{ width: '100%', padding: '0 var(--spacing-4)' }}>
        {/* Header */}
        <div className="campaign-builder-header">
          <h1 className="campaign-builder-title">Clients</h1>
          <p className="campaign-builder-subtitle">
            Manage your client configurations for streamlined campaign creation
          </p>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary btn-large"
            style={{ marginTop: 'var(--spacing-6)' }}
            disabled={loading}
          >
            + Add New Client
          </button>
        </div>

        {/* Database Clients Section */}
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <h2 style={{ 
            fontSize: 'var(--font-size-xl)', 
            fontWeight: 'var(--font-weight-semibold)', 
            color: 'var(--color-label)',
            marginBottom: 'var(--spacing-4)'
          }}>
            Your Clients
          </h2>
          <p style={{ 
            color: 'var(--color-secondaryLabel)', 
            marginBottom: 'var(--spacing-6)',
            fontSize: 'var(--font-size-base)'
          }}>
            Manage your client configurations used for campaigns, assets, and analytics.
          </p>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
              <div style={{
                width: 32,
                height: 32,
                border: '3px solid var(--color-tertiaryLabel)',
                borderTop: '3px solid var(--color-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          ) : (
            <div className="presets-grid">
              {dbClients.map((client) => (
                <div key={client.id} className="preset-card">
                  <div className="preset-header">
                    <div className="preset-logo" style={{
                      backgroundColor: client.primary_color,
                      color: 'white'
                    }}>
                      {client.brand.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="preset-info">
                      <h3 className="preset-brand">{client.brand}</h3>
                      <p className="preset-industry">{client.industry || 'No industry set'}</p>
                    </div>
                  </div>

                  <div className="preset-details">
                    <div className="preset-detail-row">
                      <span className="preset-label">Account ID:</span>
                      <span className="preset-value monospace">{client.meta_account_id}</span>
                    </div>
                    <div className="preset-detail-row">
                      <span className="preset-label">Page:</span>
                      <span className="preset-value">{client.page_name}</span>
                    </div>
                    <div className="preset-detail-row">
                      <span className="preset-label">Budget:</span>
                      <span className="preset-value">${(client.default_budget / 100).toFixed(2)}</span>
                    </div>
                    <div className="preset-detail-row">
                      <span className="preset-label">Duration:</span>
                      <span className="preset-value">{client.default_duration} days</span>
                    </div>
                  </div>

                  <div className="preset-actions">
                    <button
                      onClick={() => handleOpenModal(client)}
                      className="btn btn-secondary btn-small"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client)}
                      className="btn btn-danger btn-small"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              
              {dbClients.length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: 'var(--spacing-8)',
                  color: 'var(--color-secondaryLabel)'
                }}>
                  <p>No database clients found. Create your first client to get started!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '900px', width: '100%' }}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingClient ? `Edit Client: ${editingClient.brand}` : 'Add New Client'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="modal-close"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* Basic Info */}
                  <div className="form-section">
                    <h3 className="form-section-title">Basic Information</h3>
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Client Name</label>
                        <input
                          type="text"
                          value={formData.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Brand Name</label>
                        <input
                          type="text"
                          value={formData.brand || ''}
                          onChange={(e) => handleInputChange('brand', e.target.value)}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Meta Account Settings */}
                  <div className="form-section">
                    <h3 className="form-section-title">Meta Account Settings</h3>
                    
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Meta Account ID</label>
                        <input
                          type="text"
                          value={formData.metaAccountId || ''}
                          onChange={(e) => handleInputChange('metaAccountId', e.target.value)}
                          className="form-input monospace"
                          placeholder="act_123456789"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Page ID</label>
                        <input
                          type="text"
                          value={formData.pageId || ''}
                          onChange={(e) => handleInputChange('pageId', e.target.value)}
                          className="form-input monospace"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Page Name</label>
                        <input
                          type="text"
                          value={formData.pageName || ''}
                          onChange={(e) => handleInputChange('pageName', e.target.value)}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Pixel ID</label>
                        <input
                          type="text"
                          value={formData.pixelId || ''}
                          onChange={(e) => handleInputChange('pixelId', e.target.value)}
                          className="form-input monospace"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Instagram Account ID</label>
                        <input
                          type="text"
                          value={formData.instagramAccountId || ''}
                          onChange={(e) => handleInputChange('instagramAccountId', e.target.value)}
                          className="form-input monospace"
                          placeholder="Instagram account ID (optional)"
                        />
                        <small className="form-help">Optional: Instagram account ID for ad creatives targeting Instagram</small>
                      </div>
                    </div>
                  </div>

                  {/* Default Campaign Settings */}
                  <div className="form-section">
                    <h3 className="form-section-title">Default Campaign Settings</h3>
                    
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Default Budget ($)</label>
                        <input
                          type="number"
                          value={formData.defaultBudget || ''}
                          onChange={(e) => handleInputChange('defaultBudget', parseInt(e.target.value))}
                          className="form-input"
                          placeholder="100"
                          min="1"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Default Duration (days)</label>
                        <input
                          type="number"
                          value={formData.defaultDuration || ''}
                          onChange={(e) => handleInputChange('defaultDuration', parseInt(e.target.value))}
                          className="form-input"
                          placeholder="30"
                          min="1"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Age Range</label>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                          <input
                            type="number"
                            value={formData.defaultAgeMin || ''}
                            onChange={(e) => handleInputChange('defaultAgeMin', parseInt(e.target.value))}
                            className="form-input"
                            placeholder="Min"
                            min="13"
                            max="65"
                          />
                          <input
                            type="number"
                            value={formData.defaultAgeMax || ''}
                            onChange={(e) => handleInputChange('defaultAgeMax', parseInt(e.target.value))}
                            className="form-input"
                            placeholder="Max"
                            min="13"
                            max="65"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Default Countries */}
                    <div style={{ marginTop: 'var(--spacing-4)' }}>
                      <label className="form-label" style={{ marginBottom: 'var(--spacing-3)' }}>
                        Default Countries
                      </label>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                        gap: 'var(--spacing-2)',
                        padding: 'var(--spacing-3)',
                        background: 'var(--color-systemFill)',
                        borderRadius: '8px'
                      }}>
                        {['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'IT', 'ES'].map(country => (
                          <label key={country} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <input
                              type="checkbox"
                              checked={formData.defaultCountries?.includes(country) || false}
                              onChange={(e) => handleCountryChange(country, e.target.checked)}
                              style={{ margin: 0 }}
                            />
                            <span style={{ fontSize: 'var(--font-size-sm)' }}>{country}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Default Genders */}
                    <div style={{ marginTop: 'var(--spacing-4)' }}>
                      <label className="form-label" style={{ marginBottom: 'var(--spacing-3)' }}>
                        Default Genders
                      </label>
                      <div style={{ 
                        display: 'flex', 
                        gap: 'var(--spacing-6)',
                        padding: 'var(--spacing-3)',
                        background: 'var(--color-systemFill)',
                        borderRadius: '8px'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                          <input
                            type="checkbox"
                            checked={formData.defaultGenders?.includes(1) || false}
                            onChange={(e) => handleGenderChange(1, e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: 'var(--font-size-sm)' }}>Male</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                          <input
                            type="checkbox"
                            checked={formData.defaultGenders?.includes(2) || false}
                            onChange={(e) => handleGenderChange(2, e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: 'var(--font-size-sm)' }}>Female</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="form-section">
                    <h3 className="form-section-title">Business Information</h3>
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Website URL</label>
                        <input
                          type="url"
                          value={formData.website || ''}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          className="form-input"
                          placeholder="https://example.com"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Industry</label>
                        <select
                          value={formData.industry || ''}
                          onChange={(e) => handleInputChange('industry', e.target.value)}
                          className="form-select"
                          required
                        >
                          <option value="">Select Industry</option>
                          <option value="Health & Wellness">Health & Wellness</option>
                          <option value="E-commerce">E-commerce</option>
                          <option value="Technology">Technology</option>
                          <option value="Finance">Finance</option>
                          <option value="Real Estate">Real Estate</option>
                          <option value="Education">Education</option>
                          <option value="Travel">Travel</option>
                          <option value="Food & Beverage">Food & Beverage</option>
                          <option value="Fashion">Fashion</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* UTM Tracking */}
                  <div className="form-section">
                    <h3 className="form-section-title">UTM Tracking Template</h3>
                    <div className="form-group">
                      <label className="form-label">UTM Parameter Template</label>
                      <textarea
                        value={formData.utmTemplate || ''}
                        onChange={(e) => handleInputChange('utmTemplate', e.target.value)}
                        className="form-input monospace"
                        placeholder="utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}"
                        rows={3}
                        style={{ fontSize: '12px', lineHeight: '1.4' }}
                      />
                      <small className="form-help">
                        UTM parameters to automatically append to landing page URLs. Use Meta variables like {`{{campaign.name}}, {{adset.name}}, {{ad.name}}, {{ad.id}}, {{site_source_name}}, {{placement}}`}.
                      </small>
                    </div>
                  </div>

                  {/* Brand Colors */}
                  <div className="form-section">
                    <h3 className="form-section-title">Brand Colors</h3>
                    <div className="form-grid two-columns">
                      <div className="form-group">
                        <label className="form-label">Primary Color</label>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                          <input
                            type="color"
                            value={formData.primaryColor || 'var(--color-systemBlue)'}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            style={{ width: '60px', height: '40px', border: 'none', borderRadius: '8px' }}
                          />
                          <input
                            type="text"
                            value={formData.primaryColor || 'var(--color-systemBlue)'}
                            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                            className="form-input monospace"
                            placeholder="var(--color-systemBlue)"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Secondary Color</label>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                          <input
                            type="color"
                            value={formData.secondaryColor || 'var(--color-systemIndigo)'}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            style={{ width: '60px', height: '40px', border: 'none', borderRadius: '8px' }}
                          />
                          <input
                            type="text"
                            value={formData.secondaryColor || 'var(--color-systemIndigo)'}
                            onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                            className="form-input monospace"
                            placeholder="var(--color-systemIndigo)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 
                     editingClient ? 'Update Client' : 
                     'Create Client'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPresetsPage; 